import type { TimeRange } from '@ardoq/graph';
import type {
  DateRangeWithOptionalDates,
  ItemTops,
  TimelineLabelWithRenderingParameters,
  TimelinePhasesWithRenderingParameters,
  TimelineViewRenderingData,
  VirtualScrollState,
} from './types';
import {
  DateRangeViewSetting,
  TimelineGridPrecisionOptionValue,
} from '@ardoq/api-types';
import {
  DEFAULT_WINDOW_CENTER,
  TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE,
} from './consts';
import { isTruthy } from '@ardoq/common-helpers';
import { clamp } from 'lodash';
import {
  getRelativeDateRange,
  parseDate,
  relativeDateToISOString,
  RELATIVE_DATE_REGEX,
} from '@ardoq/date-time';
import { findItemIndexForScroll } from './scrollToItemIndex';
import { MARGIN_BETWEEN_ROWS } from './items/consts';

/**
 * To ensure backward compatibility and uniform processing of the absent grid
 * precision value, we should use this high level function for reading the
 * respective view setting, so we do not have to enforce initial state while
 * rendering components in multiple places
 */
export const readTimelineGridPrecisionViewSetting = (
  value?: TimelineGridPrecisionOptionValue | null
) => value ?? TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE;
/**
 * The dates are stored in string format, and older slides will not have this view setting.
 *
 * This ugly construct is needed for type consistency
 */
export const readTimelineDomainRangeViewSetting = (
  domainRangeViewSetting?: DateRangeViewSetting
): DateRangeWithOptionalDates => {
  if (!domainRangeViewSetting) return [null, null];

  if (Array.isArray(domainRangeViewSetting)) {
    return [
      readDateRangeSettingDate(domainRangeViewSetting[0]),
      readDateRangeSettingDate(domainRangeViewSetting[1]),
    ];
  }

  const range = getRelativeDateRange(domainRangeViewSetting);
  return range ? [range.start, range.end] : [null, null];
};

export const areDatesEffectivelyEqual = (
  date1?: Date | null,
  date2?: Date | null
) => date1?.valueOf() === date2?.valueOf();

export const areTwoDateRangesEffectivelyEqual = (
  range1: DateRangeWithOptionalDates,
  range2: DateRangeWithOptionalDates
) =>
  areDatesEffectivelyEqual(range1[0], range2[0]) &&
  areDatesEffectivelyEqual(range1[1], range2[1]);

const readDateRangeSettingDate = (
  date: string | Date | null | undefined
): Date | null => {
  if (!date) return null;
  if (date instanceof Date) return date;
  return RELATIVE_DATE_REGEX.test(date)
    ? parseDate(relativeDateToISOString(date))
    : parseDate(date);
};

export const timeSpan = (range: TimeRange): number =>
  range[1].getTime() - range[0].getTime();

export const isDateWithinRange = (tick: Date, domain: TimeRange) =>
  tick >= domain[0] && tick <= domain[1];

export const isFullDateRange = (
  optionalDateRange?: DateRangeWithOptionalDates
): optionalDateRange is TimeRange =>
  Boolean(optionalDateRange && optionalDateRange.filter(isTruthy).length === 2);

export const getWindowCenterForDate = (
  date: Date,
  chartDomain: TimeRange,
  windowScale: number
): [x: number, y: number] | null => {
  if (!isDateWithinRange(date, chartDomain)) {
    return null;
  }

  const minWindowCenterX = DEFAULT_WINDOW_CENTER[0] * windowScale;
  const maxWindowCenterX = 1 - minWindowCenterX;

  const domainRatio = timeSpan([chartDomain[0], date]) / timeSpan(chartDomain);

  return [clamp(domainRatio, minWindowCenterX, maxWindowCenterX), 0.5];
};

export const getRowHeightFromItemTops = (
  itemTops: ItemTops,
  index: number
): number => {
  // last item of itemTops is the totalHeight
  if (index < 0 || index >= itemTops.length - 1) {
    return 0;
  }
  return itemTops[index + 1] - itemTops[index] - MARGIN_BETWEEN_ROWS;
};

/**
 * Get inclusive height of all rows in range.
 * If range exceeds existing rows, it will return the height of the
 * existing rows in that range.
 */
export const getRowsHeightFromItemTops = (
  itemTops: ItemTops,
  startIndex: number,
  endIndex: number
): number => {
  // last item of itemTops is the totalHeight
  if (startIndex < 0 || startIndex >= itemTops.length - 1) {
    return 0;
  }

  if (endIndex < 0) {
    return 0;
  }

  const validEndIndex = Math.min(endIndex, itemTops.length - 2);

  let totalHeight = 0;
  for (let i = startIndex; i <= validEndIndex; i++) {
    totalHeight += getRowHeightFromItemTops(itemTops, i) + MARGIN_BETWEEN_ROWS;
  }
  return totalHeight;
};

type GetVirtualScrollStateArgs = {
  chartHeight: number;
  scrollTop: number;
  itemTops: ItemTops;
  currentIndex?: number;
};

/**
 * @returns first and last rendered items. Note that those are taking the virtual scroll overscan into account,
 * i.e. those are not the visible items, they are the rendered items.
 */
export const getVirtualScrollState = ({
  chartHeight,
  scrollTop,
  currentIndex = 0,
  itemTops,
}: GetVirtualScrollStateArgs): VirtualScrollState => {
  const indexClamped = clamp(currentIndex, 0, itemTops.length - 2);
  const scrollTopClamped = clamp(scrollTop, 0, itemTops[itemTops.length - 1]);

  const scrollWindowWithOverscanStart = Math.max(
    scrollTopClamped - chartHeight,
    0
  );
  const scrollWindowWithOverscanEnd = scrollTopClamped + chartHeight * 2;

  const firstItemIndex = findItemIndexForScroll({
    scrollTop: scrollWindowWithOverscanStart,
    currentIndex: indexClamped,
    itemTops,
  });

  const lastItemIndex = findItemIndexForScroll({
    scrollTop: scrollWindowWithOverscanEnd,
    currentIndex: firstItemIndex,
    itemTops,
  });

  return {
    firstItemIndex,
    lastItemIndex,
    scrollTop,
  };
};

type ClampRowsToVirtualWindowArgs = {
  allLabels: TimelineLabelWithRenderingParameters[];
  allPhases: TimelinePhasesWithRenderingParameters[];
  allItemTopsWithSentinel: ItemTops;
  virtualScrollState: VirtualScrollState;
};
export const clampRowsToVirtualWindow = ({
  allLabels,
  allPhases,
  allItemTopsWithSentinel,
  virtualScrollState,
}: ClampRowsToVirtualWindowArgs) => {
  const { firstItemIndex, lastItemIndex } = virtualScrollState;

  const labelsInVirtualWindow = allLabels.slice(
    firstItemIndex,
    lastItemIndex + 1
  );
  const phasesInVirtualWindow = allPhases.slice(
    firstItemIndex,
    lastItemIndex + 1
  );

  const itemTopsInVirtualWindow = allItemTopsWithSentinel.slice(
    firstItemIndex,
    lastItemIndex + 2
  );

  return {
    labelsInVirtualWindow,
    phasesInVirtualWindow,
    itemTopsInVirtualWindow,
  };
};

export const getContainerHeight = ({
  allItemTopsWithSentinel,
  timelineChartRenderingData,
  backgroundScrollHeight: previousBackgroundScrollHeight,
}: Pick<
  TimelineViewRenderingData,
  | 'allItemTopsWithSentinel'
  | 'timelineChartRenderingData'
  | 'backgroundScrollHeight'
>) => {
  const effectiveContentHeight =
    allItemTopsWithSentinel[allItemTopsWithSentinel.length - 1];

  const visibleAreaHeight =
    timelineChartRenderingData.timelineChartProperties.dimensions.container
      .height -
    (timelineChartRenderingData.timelineChartProperties.xAxisProps
      .xAxisContainerHeight +
      1);

  const minBackgroundHeight = Math.max(
    visibleAreaHeight,
    effectiveContentHeight
  );

  const isContainerHeightAdjustmentNeeded =
    minBackgroundHeight > previousBackgroundScrollHeight ||
    previousBackgroundScrollHeight - minBackgroundHeight >= visibleAreaHeight;

  return {
    backgroundScrollHeight: isContainerHeightAdjustmentNeeded
      ? minBackgroundHeight
      : previousBackgroundScrollHeight,
    visibleAreaHeight,
  };
};
